import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useState } from "react";
import {
  Moon,
  Heart,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { FavoriteButton } from "@/components/FavoriteButton";

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export const Route = createFileRoute("/sleep")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Sleep & Rest — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gentle, trauma-informed support for sleep and rest. Soft invitations for nervous-system regulation, comfort, and restoration.",
      },
      { property: "og:title", content: "Sleep & Rest — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A quiet, comforting space for rest. You do not need to earn rest. Rest is a human need.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/sleep" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/sleep" }],
  }),
  component: withMemberGate(SleepPage, "Sleep"),
});

const SECTIONS = [
  { id: "support", emoji: "🌙", title: "Gentle Sleep Support" },
  { id: "difficult", emoji: "🤍", title: "When Sleep Feels Difficult" },
  { id: "awake", emoji: "🌙", title: "If You've Been Awake" },
  { id: "reminder", emoji: "💛", title: "A Gentle Reminder" },
] as const;

const GENTLE_TIPS = [
  {
    emoji: "💡",
    title: "Create softer lighting",
    body:
      "As evening approaches, gentle lighting can help signal to the body that the day is slowing down.",
  },
  {
    emoji: "🧸",
    title: "Make your space feel comforting",
    body:
      "Warm blankets, familiar scents, calming sounds, favourite pillows, or comforting objects can help create a greater sense of ease.",
  },
  {
    emoji: "📝",
    title: "Put thoughts somewhere safe",
    body:
      "If your mind feels busy, try writing down thoughts, reminders, worries, or reflections before bed.",
  },
  {
    emoji: "📱",
    title: "Create a gentle transition",
    body:
      "If possible, allow a little space between screens and sleep. Reading, stretching, calming audio, colouring, or journaling may help the body slow down.",
  },
  {
    emoji: "🌅",
    title: "Be kind to your mornings",
    body:
      "Consistent wake times often support rest more than trying to force sleep.",
  },
  {
    emoji: "🌡️",
    title: "Support your body's natural rhythm",
    body:
      "Many people sleep best in a slightly cooler room with comfortable bedding and layers.",
  },
  {
    emoji: "🫖",
    title: "Create a bedtime ritual",
    body:
      "A warm drink, calming music, a book, aromatherapy, or a few minutes of gentle breathing can help signal safety and routine.",
  },
  {
    emoji: "🛏️",
    title: "Weighted blankets (if comfortable for you)",
    body:
      "Some people find weighted blankets comforting because the gentle pressure can create a feeling of grounding and containment. Only use what feels comfortable for your body.",
  },
  {
    emoji: "💛",
    title: "Be gentle with yourself",
    body:
      "A difficult night does not mean you have failed. Rest comes in many forms, and tomorrow is a new opportunity to begin again.",
  },
];

const AWAKE_TIPS = [
  {
    emoji: "💛",
    title: "Let go of the goal of sleep",
    body:
      "Rather than trying to force sleep, see if you can simply allow yourself to rest. Rest still matters.",
  },
  {
    emoji: "🪟",
    title: "Change the environment gently",
    body:
      "If you've been lying awake for some time, you may wish to move to a quiet, dimly lit space until you feel more settled.",
  },
  {
    emoji: "🌬️",
    title: "Lengthen the exhale",
    body:
      "Many people find it helpful to breathe in gently and breathe out a little more slowly.",
  },
  {
    emoji: "🤍",
    title: "Offer yourself comfort",
    body:
      "A hand on the heart, a blanket around your shoulders, a weighted blanket, a soft pillow, or a favourite comfort object can sometimes help the body feel less alone.",
  },
  {
    emoji: "🎵",
    title: "Try a calming resource",
    body:
      "Sleep sounds, gentle meditations, relaxation audio, or soothing music may help create a greater sense of calm.",
  },
  {
    emoji: "🛏️",
    title: "Return without pressure",
    body:
      "When your body begins to feel heavier or more settled, you can gently return to bed without expecting sleep to happen immediately.",
  },
];

function SleepPage() {
  const [active, setActive] = useState<string>("support");

  useEffect(() => {
    const onScroll = () => {
      const offsets = SECTIONS.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 120) };
      });
      offsets.sort((a, b) => a.top - b.top);
      setActive(offsets[0].id);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-dvh bg-gradient-to-b from-background via-background to-muted/30">
      {/* Hero */}
      <header className="mx-auto max-w-3xl px-4 pt-10 pb-6 text-center sm:pt-14">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
          <Moon className="h-6 w-6 text-primary" aria-hidden />
        </div>
        <h1 className="text-3xl font-light tracking-tight text-foreground sm:text-4xl">
          🌙 Sleep &amp; Rest
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Sleep is not something we can force. This space offers gentle support
          for rest, relaxation, and nervous system regulation.
        </p>
        <p className="mx-auto mt-2 max-w-md text-sm italic text-foreground/70">
          Take what feels helpful and leave what doesn't.
        </p>
      </header>

      {/* Section nav */}
      <nav
        className="sticky top-0 z-20 mx-auto -mt-2 mb-8 max-w-3xl border-b border-border/40 bg-background/80 px-2 py-3 backdrop-blur"
        aria-label="Sleep sections"
      >
        <ul className="flex gap-2 overflow-x-auto px-2 sm:justify-center">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active === s.id
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border/60 bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                <span aria-hidden>{s.emoji}</span>
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <main className="mx-auto max-w-3xl space-y-14 px-4 pb-24">
        {/* Gentle Sleep Support */}
        <Section id="support" emoji="🌙" title="Gentle Sleep Support">

          <p className="text-muted-foreground">
            Small invitations that may help your body recognise it is time to
            rest.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {GENTLE_TIPS.map((tip) => (
              <li
                key={tip.title}
                className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card p-4"
              >
                <div className="flex-1">
                  <p className="flex items-start gap-2 text-sm font-medium text-foreground">
                    <span aria-hidden>{tip.emoji}</span>
                    <span>{tip.title}</span>
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {tip.body}
                  </p>
                </div>
                <FavoriteButton
                  storageKey="rt:fav-sleep"
                  id={`gentle-${slugify(tip.title)}`}
                  label={tip.title}
                  sub="Gentle sleep support"
                  to="/sleep"
                  size="sm"
                />
              </li>
            ))}
          </ul>
        </Section>

        {/* When Sleep Feels Difficult */}
        <Section id="difficult" emoji="🤍" title="When Sleep Feels Difficult">
          <div className="space-y-3 rounded-2xl border border-border/60 bg-card p-5 text-sm leading-relaxed text-muted-foreground">
            <p>
              For some people, especially after stressful or difficult life
              experiences, sleep can feel vulnerable.
            </p>
            <p>
              A busy mind, racing thoughts, tension, vivid dreams, nightmares,
              or frequent waking can all be understandable responses from a
              nervous system that is trying to stay alert.
            </p>
            <p className="italic text-foreground/80">
              You do not need to fight your body.
            </p>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
            <p className="text-sm text-foreground">
              <span className="font-medium">If the night feels overwhelming:</span>{" "}
              you don't have to do this alone.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/crisis"
                className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
              >
                Crisis support <ChevronRight className="h-3 w-3" />
              </Link>
              <Link
                to="/community"
                className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
              >
                Community <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </Section>

        {/* If You've Been Awake */}
        <Section
          id="awake"
          emoji="🌙"
          title="If You've Been Awake For A While"
        >
          <ul className="space-y-3">
            {AWAKE_TIPS.map((tip) => (
              <li
                key={tip.title}
                className="flex items-start gap-3 rounded-2xl border border-border/60 bg-card p-4"
              >
                <div className="flex-1">
                  <p className="flex items-start gap-2 text-sm font-medium text-foreground">
                    <span aria-hidden>{tip.emoji}</span>
                    <span>{tip.title}</span>
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {tip.body}
                  </p>
                </div>
                <FavoriteButton
                  storageKey="rt:fav-sleep"
                  id={`awake-${slugify(tip.title)}`}
                  label={tip.title}
                  sub="If you've been awake"
                  to="/sleep"
                  size="sm"
                />
              </li>
            ))}
          </ul>
        </Section>

        {/* A Gentle Reminder */}
        <Section id="reminder" emoji="💛" title="A Gentle Reminder">
          <div className="rounded-3xl border border-primary/20 bg-primary/5 p-6 text-center">
            <Heart className="mx-auto h-6 w-6 text-primary" aria-hidden />
            <div className="mx-auto mt-4 max-w-lg space-y-3 text-sm leading-relaxed text-foreground/85">
              <p>You do not need to earn rest.</p>
              <p>You do not need to sleep perfectly.</p>
              <p>
                Your body is doing the best it can with the information it has
                right now.
              </p>
              <p>
                Even quiet moments of comfort, stillness, and restoration
                matter.
              </p>
              <p className="pt-2 font-serif text-base italic text-foreground">
                Rest is not a reward. Rest is a human need.
              </p>
            </div>
          </div>
        </Section>

        {/* Companion spaces */}
        <section className="rounded-2xl border border-border/60 bg-card p-6">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-foreground">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            Companion spaces
          </h2>
          <ul className="grid gap-2 text-sm sm:grid-cols-2">
            <CompanionLink to="/breathing" emoji="🌬️" label="Gentle Breathing" desc="Long exhales to soften the body." />
            <CompanionLink to="/sounds" emoji="🎧" label="Sleep Sounds" desc="Rain, ocean, fire & soft music." />
            <CompanionLink to="/meditations" emoji="🧘" label="Meditations" desc="Guided audio for body and mind." />
            <CompanionLink to="/dreams" emoji="🌙" label="Dream Journal" desc="Record and reflect on the night." />
            <CompanionLink to="/sleep-dreams" emoji="📊" label="Sleep Tracking" desc="Notice patterns gently over time." />
            <CompanionLink to="/grounding" emoji="🌿" label="Grounding" desc="Soft anchors when the mind is loud." />
          </ul>
        </section>

        <p className="text-center text-xs text-muted-foreground">
          Recovery Trauma™ · Healing Beyond Survival Mode™
        </p>
      </main>

    </div>
  );
}




function Section({
  id,
  title,
  emoji,
  children,
}: {
  id: string;
  title: string;
  emoji: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4">
      <h2 className="flex items-center gap-2 text-xl font-light text-foreground sm:text-2xl">
        <span aria-hidden>{emoji}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

function CompanionLink({
  to,
  emoji,
  label,
  desc,
}: {
  to: string;
  emoji: string;
  label: string;
  desc: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-start gap-3 rounded-xl border border-border/40 bg-background p-3 transition-colors hover:bg-accent"
      >
        <span className="text-lg" aria-hidden>{emoji}</span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-foreground">{label}</span>
          <span className="block text-xs text-muted-foreground">{desc}</span>
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
      </Link>
    </li>
  );
}
